import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  DefectAssistantImage,
  DefectAssistantSuggestion,
  fetchDefectCodeAdviceAction,
  isQuestionUnanswered,
  OptionValue,
  QnA,
  QuestionTypes,
  ReportDefectBody,
} from '../actions/defectAssistant'
import type { RootState } from '../index'
import { getDefectGroupsLabel } from '../lib/createDefectFormUtils'
import type { ActionStatus } from '../types/Input'

export type AssistantPhase = 'suggestions' | 'questions' | 'failed'

export type DefectCodeAdviceStatus = 'loading' | 'ready' | 'failed'

export interface DefectCodeAdviceEntry {
  status: DefectCodeAdviceStatus
  text: string
}

export interface CreateDefectAssistantState {
  phase: AssistantPhase
  additionalDetailsVisible: boolean
  suggestionAttempts: number
  analyzeStatus: ActionStatus
  questionStatus: ActionStatus
  images: DefectAssistantImage[]
  baseComment: string
  // Saatetaan tehdä enemmänkin näitä jos muissa kentissä kysytään lisätietoja
  // tai sitten vain yleinen discardedPile
  discardedDefectCodes: string[]
  suggestions: DefectAssistantSuggestion[]
  metaOptions: OptionValue[]
  // Identifies the in-flight suggestion stream used to pull each prefix's defectCodeAdvice.
  generationId: string | null
  // Per-generation "why to choose" defectCodeAdvice (generationId → prefix → entry), filled first-ready-first
  // as each fetch resolves. Keyed by generationId — not a single flat map — so a discarded round's
  // defectCodeAdvice keeps streaming into its locked block even after the user has started a new round; each
  // round renders its own generation's map. A missing entry is treated as still loading by the UI.
  defectCodeAdviceByGeneration: Record<string, Record<string, DefectCodeAdviceEntry>>
  body: ReportDefectBody
  questionAnswers: QnA[]
}

type InitializeCreateDefectAssistantPayload = {
  comment: string
  images: DefectAssistantImage[]
  vehicleType: string
  installationGroup: string
}

const initialState: CreateDefectAssistantState = {
  phase: 'suggestions',
  additionalDetailsVisible: false,
  suggestionAttempts: 1,
  analyzeStatus: 'none',
  questionStatus: 'none',
  images: [],
  baseComment: '',
  discardedDefectCodes: [],
  suggestions: [],
  metaOptions: [],
  generationId: null,
  defectCodeAdviceByGeneration: {},
  body: {
    userComment: '',
    aiComment: '',
    defectCode: '',
    defectType: '',
    installationGroup: '',
    vehicleSeries: '',
  },
  questionAnswers: [],
}

// Records one prefix's defectCodeAdvice under its generation, creating the generation's bucket on first write.
// Going through the draft on every access keeps Immer's change tracking intact for the nested write.
const setDefectCodeAdvice = (
  state: CreateDefectAssistantState,
  generationId: string,
  prefix: string,
  entry: DefectCodeAdviceEntry
): void => {
  if (!state.defectCodeAdviceByGeneration[generationId]) {
    state.defectCodeAdviceByGeneration[generationId] = {}
  }
  state.defectCodeAdviceByGeneration[generationId][prefix] = entry
}

export const createDefectAssistantSlice = createSlice({
  name: 'createDefectAssistant',
  initialState,
  reducers: {
    initializeCreateDefectAssistant: (
      _state,
      { payload }: PayloadAction<InitializeCreateDefectAssistantPayload>
    ) => ({
      ...initialState,
      images: payload.images,
      baseComment: payload.comment.trim(),
      body: {
        userComment: payload.comment.trim(),
        aiComment: '',
        defectCode: '',
        defectType: '',
        installationGroup: payload.installationGroup,
        vehicleSeries: payload.vehicleType,
      },
    }),

    // Voidaanko vain nollata koko homma? Mitä tehdään virheen sattuessa?
    clearCreateDefectAssistantErrors: (state) => {
      if (state.analyzeStatus === 'failed') {
        state.analyzeStatus = 'none'
      }
      if (state.questionStatus === 'failed') {
        state.questionStatus = 'none'
      }
      if (state.phase === 'failed') {
        state.phase = 'suggestions'
      }
    },

    selectSuggestion: (state, { payload }: PayloadAction<DefectAssistantSuggestion>) => {
      state.body.defectCode = payload.defectCode
      // Re-picking a different code invalidates everything downstream of it. Clear the dependent body
      // fields too — otherwise a stale defectType/itemId in the body makes the backend treat those
      // questions as already answered and skip them, leaving empty placeholder sections.
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      // Same short-circuit, worse symptom: the backend skips the limitation question outright while
      // restrictionCode is set, so a Palvelurajoite confirmed for the code we just replaced would
      // never be re-asked and would ride into the form as if the worker had confirmed it here.
      state.body.restrictionCode = ''
      state.questionAnswers = []
      state.questionStatus = 'none'
      state.phase = 'questions'
    },

    appendCurrentComment: (state, { payload }: PayloadAction<string>) => {
      const trimmedDetails = payload.trim()
      if (trimmedDetails.length === 0) {
        return
      }

      state.body.userComment =
        state.body.userComment.length > 0
          ? `${state.body.userComment}\n\n${trimmedDetails}`
          : trimmedDetails
    },

    applyQuestionAnswer: (state, { payload }: PayloadAction<OptionValue[]>) => {
      const targetIdx = state.questionAnswers.findIndex(isQuestionUnanswered)
      if (targetIdx === -1) {
        return
      }

      const targetQnA = state.questionAnswers[targetIdx]
      state.questionAnswers = state.questionAnswers.map((qna, idx) =>
        idx === targetIdx ? { ...qna, answers: payload } : qna
      )

      // Optimistic body updates; the backend recomputes body from the answers on the next round.
      const first = payload[0]
      if (first?.value != null) {
        if (targetQnA.questionType === QuestionTypes.DEFECT_TYPE) {
          state.body.defectType = String(first.value)
        }
        if (targetQnA.questionType === QuestionTypes.PHYSICAL_ITEM) {
          state.body.itemId = String(first.value)
        }
      }
    },

    excludeSuggestions: (state, { payload }: PayloadAction<string[]>) => {
      const existing = new Set(state.discardedDefectCodes)
      payload.forEach((code) => {
        existing.add(code)
      })
      state.discardedDefectCodes = Array.from(existing)
      state.suggestionAttempts = state.suggestionAttempts + 1
      state.additionalDetailsVisible = true
    },

    resetAnswersFromIndex: (state, { payload }: PayloadAction<number>) => {
      // Clear the body fields populated by the question being re-edited and any dropped after it.
      // Otherwise a stale defectType/itemId in the body makes the backend treat those questions as
      // already resolved and skip re-asking them, leaving empty placeholder sections (e.g. changing
      // defectType while a stale itemId remains makes the backend skip the physicalItem question).
      state.questionAnswers.slice(payload).forEach((qna) => {
        if (qna.questionType === QuestionTypes.DEFECT_TYPE) state.body.defectType = ''
        if (qna.questionType === QuestionTypes.PHYSICAL_ITEM) state.body.itemId = undefined
      })

      // Unanswer the target question and drop everything after it so that extra questions
      // (duplicate check, summary) don't show as ghost stubs and the backend re-evaluates fully.
      state.questionAnswers = state.questionAnswers
        .slice(0, payload + 1)
        .map((qna, idx) => (idx >= payload ? { ...qna, answers: null } : qna))

      // Clear aiComment: the backend skips regeneration when aiComment is already set in the body,
      // so we must clear it here to force a fresh description on re-submission.
      state.body.aiComment = ''
    },

    resetCreateDefectAssistant: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(analyzeSuggestionsAction.pending, (state) => {
      state.phase = 'suggestions'
      state.additionalDetailsVisible = false
      state.analyzeStatus = 'loading'
      state.suggestions = []
      state.metaOptions = []
      state.generationId = null
      // Deliberately keep defectCodeAdviceByGeneration: the just-discarded round's defectCodeAdvice (under its own
      // generationId) must keep filling in while this new round loads. It's cleared wholesale on
      // assistant init/reset (initialState), which is the only point a generation is truly gone.
    })

    builder.addCase(analyzeSuggestionsAction.fulfilled, (state, { payload }) => {
      state.analyzeStatus = 'succeeded'
      // If the user picked a suggestion (from an earlier round) while this fetch was in flight,
      // honour that choice — don't clobber the questions flow with stale suggestions.
      if (state.phase === 'questions') return
      state.suggestions = payload.suggestions
      state.metaOptions = payload.metaOptions
      state.generationId = payload.generationId
      state.phase = 'suggestions'
    })

    // Per-prefix defectCodeAdvice fetches resolve independently and are filed under their request's
    // generationId (from action.meta.arg, not state.generationId — by the time a late fetch resolves
    // the user may already be on a newer round). A missing entry renders as loading, so we only need
    // to record terminal states (ready/failed); pending is recorded for completeness.
    builder.addCase(fetchDefectCodeAdviceAction.pending, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'loading',
        text: '',
      })
    })

    builder.addCase(fetchDefectCodeAdviceAction.fulfilled, (state, action) => {
      const { prefix, defectCodeAdvice, ready } = action.payload
      setDefectCodeAdvice(
        state,
        action.meta.arg.generationId,
        prefix,
        ready && defectCodeAdvice
          ? { status: 'ready', text: defectCodeAdvice }
          : { status: 'failed', text: '' }
      )
    })

    builder.addCase(fetchDefectCodeAdviceAction.rejected, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'failed',
        text: '',
      })
    })

    builder.addCase(analyzeSuggestionsAction.rejected, (state) => {
      state.analyzeStatus = 'failed'
      state.phase = 'failed'
    })

    builder.addCase(advanceQuestionFlowAction.pending, (state) => {
      state.phase = 'questions'
      state.additionalDetailsVisible = false
      state.questionStatus = 'loading'
    })

    builder.addCase(advanceQuestionFlowAction.fulfilled, (state, { payload }) => {
      state.questionStatus = 'succeeded'
      state.body = {
        ...payload.body,
        // Sometimes backend doesn't return installationGroup or vehicleSeries
        installationGroup: payload.body.installationGroup ?? state.body.installationGroup,
        vehicleSeries: payload.body.vehicleSeries ?? state.body.vehicleSeries,
        // Distinguish "omitted" from "explicitly cleared": undefined means the backend left itemId
        // out, so keep the value resolved in a prior round; null means it was explicitly cleared
        // (e.g. the physical item resolved to NONE), so drop the stale id.
        itemId:
          payload.body.itemId === undefined ? state.body.itemId : payload.body.itemId ?? undefined,
      }
      state.questionAnswers = payload.questionAnswers
      state.phase = 'questions'
    })

    builder.addCase(advanceQuestionFlowAction.rejected, (state, action) => {
      if (action.meta.aborted) return
      state.questionStatus = 'failed'
      state.phase = 'failed'
    })
  },
})

export const {
  initializeCreateDefectAssistant,
  clearCreateDefectAssistantErrors,
  selectSuggestion,
  appendCurrentComment,
  applyQuestionAnswer,
  excludeSuggestions,
  resetAnswersFromIndex,
  resetCreateDefectAssistant,
} = createDefectAssistantSlice.actions

export const selectCreateDefectAssistantState = (state: RootState): CreateDefectAssistantState =>
  state.createDefectAssistant

export const selectAnsweredQuestions = (state: RootState): QnA[] => {
  const answers = state.createDefectAssistant.questionAnswers
  const firstUnansweredIdx = answers.findIndex(isQuestionUnanswered)
  return firstUnansweredIdx >= 0 ? answers.slice(0, firstUnansweredIdx) : answers
}

export const selectCurrentQuestion = (state: RootState): QnA | undefined =>
  state.createDefectAssistant.questionAnswers.find(isQuestionUnanswered)

export const selectAssistantIsComplete = (state: RootState): boolean => {
  const qnas = state.createDefectAssistant.questionAnswers
  return qnas.length > 0 && qnas.every((qna) => !isQuestionUnanswered(qna))
}

export const selectProductGroupLabel =
  (state: RootState) =>
  (productGroupPrefix: string): string => {
    const treeRow = state.defect.defectTrees.data.find((row) =>
      row.defectCode.startsWith(productGroupPrefix)
    )
    if (!treeRow) return ''
    return getDefectGroupsLabel(treeRow)
  }

export default createDefectAssistantSlice.reducer
